import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { styleSheet } from './styles';

interface DonationAmountDistributionChartProps {
	data?: Api.IDonationAmountDistributionDataPoint[];
	isLoading?: boolean;
	className?: string;
}

export const DonationAmountDistributionChart: React.FC<DonationAmountDistributionChartProps> = ({
	data,
	isLoading,
	className,
}) => {
	const chartData = React.useMemo<Api.IDonationAmountDistributionDataPoint[]>(() => {
		if (!data || data.length === 0) {
			return [];
		}

		// Define order for ranges
		const rangeOrder = [
			'$0',
			'$1-$24',
			'$25-$49',
			'$50-$99',
			'$100-$249',
			'$250-$499',
			'$500-$999',
			'$1,000-$2,499',
			'$2,500-$4,999',
			'$5,000+',
		];

		// Sort by predefined order
		const sortedData = [...data]
			.map(item => {
				const color = mainChartColor();
				return {
					...item,
					color,
				};
			})
			.sort((a, b) => {
				const indexA = rangeOrder.indexOf(a.range);
				const indexB = rangeOrder.indexOf(b.range);
				return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
			});

		// Remove all leading entries with count = 0
		const firstNonZeroIndex = sortedData.findIndex(item => item.count > 0);
		if (firstNonZeroIndex === -1) {
			// All entries have count = 0, return empty array
			return [];
		}
		return sortedData.slice(firstNonZeroIndex);
	}, [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No donation data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{tooltipData.range}</p>
					<p className={css(styleSheet.tooltipValue)}>Count: {tooltipData.count}</p>
					<p className={css(styleSheet.tooltipValue)}>Total: {formatCurrency(tooltipData.amount)}</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title)}>Donation Amount Distribution</h3>
			<ResponsiveContainer width='100%' height={400}>
				<BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='range'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						angle={-45}
						textAnchor='end'
						height={80}
					/>
					<YAxis stroke={THEME.colors.text.main} tick={{ fontSize: 12, fill: THEME.colors.text.main }} />
					<Tooltip content={<CustomTooltip />} />
					<Bar dataKey='count' radius={[4, 4, 0, 0]}>
						{chartData.map(item => {
							return <Cell key={item.range} fill={item.color} />;
						})}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
